import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router";
import { Box, Stack, LinearProgress, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import SectionLoader from "../../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../../component/EmptyState";
import RecordStat, { RecordStatRow } from "../RecordStat";
import { StatusChip } from "../../../../../../../../component/listing";
import { getEmployeeGoals, deleteGoal, getReviewCycles } from "../../../../../../../../services/performance";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import { getTokenInfo } from "../../../../../../../../services/api";
import GoalFormDialog from "../../../../../../../../component/performance/GoalFormDialog";
import { tokens, displayFont } from "../../../../../../../../theme";

const STATUS_TONE = { not_started: "muted", in_progress: "info", completed: "success", cancelled: "danger" };
const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };

const ProfileGoals = () => {
  const notify = useNotify();
  // delete is admin-only server-side; offering it to a manager would only
  // produce a 403
  const isAdmin = (getTokenInfo()?.accessTokenData?.newEffectivePermissions || []).includes("All");
  const [, employeeId] = useOutletContext();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cycles, setCycles] = useState([]);

  const fetchData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true); setError(false);
    try {
      const res = await getEmployeeGoals(employeeId);
      // cycles populate the picker; a failure here should not block the goals
      getReviewCycles().then((c) => setCycles(Array.isArray(c?.data) ? c.data : [])).catch(() => {});
      if (res?.success === false) throw new Error(res?.message);
      setGoals(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load goals"); }
    finally { setLoading(false); }
  }, [employeeId, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setDialog(true); };
  const openEdit = (g) => { setEditing(g._id); setDialog(true); };

  const remove = async (id) => {
    try { const res = await deleteGoal(id); if (res?.success === false) throw new Error(res?.message); notify.success("Goal removed"); fetchData(); }
    catch (e) { notify.error(e?.message || "Could not remove"); }
  };

  /**
   * The book leads this panel with four counters. "3 of 4 on track or met" is
   * the sentence a reviewer wants; a list of goal cards makes them count.
   */
  const stats = useMemo(() => {
    const live = goals.filter((g) => g.status !== "cancelled");
    const met = live.filter((g) => g.status === "completed");
    const onTrack = live.filter((g) => g.status === "in_progress" && (g.progress || 0) >= 50);
    const atRisk = live.filter((g) => g.status === "in_progress" && (g.progress || 0) < 50);
    const notStarted = live.filter((g) => g.status === "not_started");
    const avg = live.length
      ? Math.round(live.reduce((t, g) => t + (Number(g.progress) || 0), 0) / live.length)
      : null;
    return { live, met, onTrack, atRisk, notStarted, avg };
  }, [goals]);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Goals &amp; KPIs</Box>
        <BtnPrimary onClick={openNew} sx={{ borderRadius: "8px" }}>Add goal</BtnPrimary>
      </Stack>

      <RecordStatRow>
        <RecordStat label="Goals set" value={stats.live.length} sub={stats.notStarted.length ? `${stats.notStarted.length} not started` : "all under way"} />
        <RecordStat label="Met" value={stats.met.length} sub={stats.live.length ? `of ${stats.live.length} this cycle` : "none set"} tone={stats.met.length ? undefined : undefined} />
        <RecordStat label="At risk" value={stats.atRisk.length} sub={stats.atRisk.length ? "under half way" : "none behind"} tone={stats.atRisk.length ? tokens.warningText : undefined} />
        <RecordStat label="Average progress" value={stats.avg == null ? "—" : `${stats.avg}%`} sub={stats.live.length ? `across all ${stats.live.length}` : "nothing to average"} />
      </RecordStatRow>

      {loading ? <SectionLoader /> : error ? <EmptyState variant="error" title="Couldn't load goals" onRetry={fetchData} /> : goals.length === 0 ? (
        <EmptyState title="No goals yet" description="Set objectives and measurable key results to track performance." />
      ) : (
        <Stack spacing={1.5}>
          {goals.map((g) => (
            <Box key={g._id} sx={{ p: "1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "0.5rem" }}>
                <Box>
                  <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{g.objective}</Box>
                  {g.description && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>{g.description}</Box>}
                  {/* a goal an employee wrote for themselves is a different
                      thing from one their manager set; createdBy says which */}
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
                    {String(g.createdBy || "") === String(employeeId) ? "Set by the employee" : "Set by their manager"}
                    {g.reviewCycle?.name ? ` · ${g.reviewCycle.name}` : ""}
                  </Box>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <StatusChip label={STATUS_LABEL[g.status]} tone={STATUS_TONE[g.status]} />
                  {/* a running cycle fixes the objective and weight, so the edit
                      affordance goes with them rather than opening a form the
                      server will refuse */}
                  <Tooltip title={g.locked?.reason || ""} arrow disableHoverListener={!g.locked?.definition}>
                    <Box component="span">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(g)}
                        disabled={Boolean(g.locked?.definition)}
                        sx={{ color: tokens.textMuted }}
                      >
                        {g.locked?.definition ? <LockOutlinedIcon sx={{ fontSize: "1rem" }} /> : "✎"}
                      </IconButton>
                    </Box>
                  </Tooltip>
                  {/* deleting is admin-only: a manager cancels instead, which
                      keeps the record of what was agreed */}
                  {isAdmin && (
                    <IconButton size="small" onClick={() => remove(g._id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: g.keyResults?.length ? "0.75rem" : 0 }}>
                <LinearProgress variant="determinate" value={g.progress || 0} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, minWidth: "3rem" }}>{g.progress || 0}%</Box>
              </Stack>
              {(g.keyResults || []).map((k, i) => (
                <Box key={i} sx={{ fontSize: "0.8125rem", color: tokens.textMuted, display: "flex", justifyContent: "space-between", py: "0.25rem" }}>
                  <span>{k.title}</span><span>{k.current ?? 0}{k.target ? ` / ${k.target}` : ""} {k.unit || ""}</span>
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      )}

      <GoalFormDialog
        open={dialog}
        goal={editing ? goals.find((g) => g._id === editing) : null}
        employeeId={employeeId}
        cycles={cycles}
        notify={notify}
        onClose={() => setDialog(false)}
        onSaved={fetchData}
      />
    </Box>
  );
};

export default ProfileGoals;
